import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

// Get CSRF token from meta tag
const getCSRFToken = (): string | null => {
  const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
  return token || null;
};

// Create axios instance with default configuration
const createAxiosClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: '/',
    timeout: 10000,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  });

  // Request interceptor to add CSRF token
  client.interceptors.request.use(
    (config) => {
      const token = getCSRFToken();
      if (token) {
        config.headers['X-CSRF-Token'] = token;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor for error handling
  client.interceptors.response.use(
    (response: AxiosResponse) => {
      return response;
    },
    (error) => {
      // Handle common Rails errors
      if (error.response?.status === 422) {
        // Validation errors - let the calling code handle these
        return Promise.reject(error);
      }
      
      if (error.response?.status === 401) {
        // Unauthorized - redirect to login or show error
        console.error('Unauthorized access');
        return Promise.reject(error);
      }
      
      if (error.response?.status === 500) {
        // Server error
        return Promise.reject(error);
      }

      if (error.response?.status === 404) {
        // Not found
        return Promise.reject(error);
      }
      
      return Promise.reject(error);
    }
  );

  return client;
};

// Create the client instance
const client = createAxiosClient();

// Export convenience methods
export const api = {
  get: <T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => 
    client.get(url, config),
  
  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => 
    client.post(url, data, config),
  
  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => 
    client.put(url, data, config),
  
  patch: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => 
    client.patch(url, data, config),
  
  delete: <T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => 
    client.delete(url, config),
};

// Export the client instance for advanced usage
export default client;

// Type definitions for common API responses
export interface ApiResponse<T = any> {
  data: T;
  message?: string;
  status: 'success' | 'error';
}

export interface ValidationError {
  field: string;
  message: string;
}

export interface ApiError {
  message: string;
  errors?: ValidationError[];
  status: number;
}
